import { Button } from '@playcastdotio/Shadcn';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { download } from '../../utility/api';
import { faWindows } from '@fortawesome/free-brands-svg-icons';
import { isMobileDevice } from '../../utility/base';
import { useEffect } from 'react';

export const FAQs = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  });

  return (
    <div className='w-full h-auto m-0 p-0 bg-content text-content-text'>
      <div className='w-full h-auto m-0 p-0 bg-hero bg-cover bg-no-repeat bg-content text-content-text'>
        <div className='container mx-auto w-full h-auto pt-20 pb-8 px-8'>
          <section id='hero' className='grid grid-cols-12 gap-y-8 gap-x-4'>
            <div className='col-span-full md:col-span-6 grid grid-rows content-center gap-y-4 lg:gap-y-8'>
              <h1 className='row-auto font-bold text-3xl lg:text-4xl'>Frequently Asked Questions</h1>
              <p className='row-auto text-lg'>Providing answers to questions we hear often!</p>
              <div className='row-auto flex flex-row gap-2'>
                {!isMobileDevice() && (
                  <Button className='rounded-full flex space-x-2' onClick={download}>
                    <FontAwesomeIcon icon={faWindows} className='text-2xl' />
                    <span>Download</span>
                  </Button>
                )}
              </div>
            </div>

            <div className='col-span-full md:col-span-5 md:col-start-8 content-center'>
              <img src='/images/preview-image.png' alt='screenshot' />
            </div>
          </section>
        </div>
      </div>

      <div className='w-full h-auto m-0 bg-content-secondary text-content-text'>
        <div className='container w-full h-auto mx-auto px-0 py-20'>
          {/* grid grid-cols-12 gap-10 md:gap-8 items-start */}
          <div className='grid grid-cols-1 md:grid-cols-2 gap-8 px-4'>
            {/* col-span-full md:col-span-6 grid grid-rows gap-y-4  items-start */}
            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl text-primary font-bold'>General Questions</h2>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>What is Playcast?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Playcast is a brand-new way to share and play games online with friends. It enables you to host your games with full
                  interactivity from your home PC to anyone you wish to play with. With Playcast, friends can seamlessly play games on your
                  computer from a remote location, as if they were physically present beside you, and you can do the same with their games!
                </p>
                <p className='row-auto pb-8 px-16 space-y-4'>
                  Simply download the Playcast App, start a session, and share the invite link with whomever you want to join. They’ll be
                  able to play from any device without having to download anything or make an account.
                </p>
                <p className='row-auto pb-8 px-16 space-y-4'>
                  Additionally, hosts can stream fully interactive games publicly by integrating with Open Broadcaster Software (OBS) for
                  streaming to platforms like Twitch and YouTube.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>How does Playcast work?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Install the Playcast App on your home PC, and make your entire game library shareable. Launch a session, and send an
                  invite link to your friends, enabling them to join your gaming session and play games using their own mouse, keyboard,
                  gamepad, or touchscreen from any browser or device.
                </p>
                <p className='row-auto pb-8 px-16 space-y-4'>
                  The Playcast App operates similarly to Open Broadcaster Software (OBS), but instead of merely capturing gameplay video and
                  sending it to a video service like Twitch, it directly streams the video to any number of player browsers. It also
                  captures their player inputs and sends them back to your Playcast App, as if their remote input devices were physically
                  connected to your local PC.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>Do I need to download any software to use Playcast?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  If you’d like to host your games for others to play, download the Playcast App from the top of this page. Participants who
                  join do not need to download anything.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>What devices can I use with Playcast?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  The Playcast App runs on Windows PCs. Participants joining a hosted session can play from any device.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>Is Playcast free to use?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Playcast is completely free for hosting and joining play sessions. Premium offerings for professional streamers and game
                  publishers may be available in the future.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>How do I invite friends to play games with me on Playcast?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Invite friends to play games with you by sharing an invite URL generated by the Playcast App.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>
                  Where can I find help if I encounter any issues while using Playcast?
                </summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  If you encounter any issues while using Playcast, you can refer to our <a href='https://discord.gg/7PYGxU68WH'>Discord</a>{' '}
                  for troubleshooting guides and to get support for further assistance.
                </p>
              </details>
            </section>

            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl text-primary font-bold'>Technical Questions</h2>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>Is Playcast secure?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  The Playcast App is a powerful remote desktop sharing application. Hosts can grant themselves and trusted friends and
                  family complete remote access to their PC, and hosts should be judicious with whom they grant this access to. To ensure
                  optimally secure interactions, please make sure to only capture and send video of relevant sources (like the game you’re
                  hosting). Additionally, we recommend limiting input access for participants to gamepad only.
                </p>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Playcast takes security and privacy seriously and is developing additional measures to ensure that gameplay sessions are
                  secure and protected. Playcast invitation links are encrypted and can’t be discovered by anyone you don’t share them with.
                  Playcast hosts are also required to 2FA authenticate their servers if they are sharing access to a Windows Admin account
                  to ensure that only trusted users can share access to a PC online.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>What happens if there's lag during a game session?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Playcast strives to minimize lag during gameplay sessions, but if lag occurs, you can try troubleshooting your internet
                  connection or adjusting your settings for optimal performance.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>
                  How do I know if my internet connection is suitable for using Playcast?
                </summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Playcast requires a stable internet connection with sufficient upload bandwidth to stream games smoothly. You can check
                  your internet speed and latency using online tools to ensure optimal performance. Generally 20Mb/s of upload bandwidth is
                  sufficient for a high quality gaming experience for 1-3 additional player desktop gaming sessions or up to 8 mobile
                  players.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>
                  Is there a limit to the number of players in a game session?
                </summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Most Playcast host users have enough upload bandwidth for Playcast live gameplay with 3-4 friends. Additional spectators
                  can also join to watch and play at the host’s discretion.
                </p>
              </details>
            </section>

            <section className='flex flex-col gap-y-4'>
              <h2 className='text-2xl text-primary font-bold'>Compatibility & Features</h2>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>
                  Can I use Playcast and stream my gameplay to platforms like Twitch or YouTube?
                </summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Yes, you can stream your Playcast sessions to platforms like Twitch or YouTube, allowing you to get your viewers involved
                  and share your gaming experiences with a wider audience.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>Can I use Playcast to play games offline?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  No, Playcast requires an internet connection to stream games and interact with other players in real-time.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>
                  Can I use Playcast to play games with friends who are located in different countries?
                </summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Yes! Playcast allows players to connect and play games with friends from anywhere in the world, as long as all
                  participants have a stable internet connection. Some games may be unsuitable for streaming over extremely large distances
                  due to latency.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>
                  Does Playcast support local multiplayer games, such as split-screen and couch co-op?
                </summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Yes! Playcast turns all local split-screen and couch co-op style games into online multiplayer games. Each remote player
                  will need their own device to join a game.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>
                  Can I use Playcast to share my gameplay with others who don't own the game?
                </summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Yes, Playcast allows you to share your gameplay with friends who don't own the game or gaming devices that support the
                  game. No downloading, installing or registration is required for those participating in your session.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>Is Playcast compatible with virtual reality (VR) gaming?</summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  While Playcast does not currently support VR gaming directly, you can still stream non-VR games to VR headsets and play
                  them in a virtual environment.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>
                  Are there any restrictions on the types of games I can play on Playcast?
                </summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Playcast can stream any Windows game, application, or media. Hosts are responsible for conforming to any licensing
                  restrictions that may be associated with the software and media that they own.
                </p>
              </details>

              <details className='row-auto border border-gray-600 rounded-lg bg-content'>
                <summary className='row-auto py-5 px-6 text-lg italic'>
                  Can I use Playcast to host private gaming sessions with a select group of friends?
                </summary>
                <p className='row-auto pt-2 pb-8 px-16 space-y-4'>
                  Yes, there is no limit to the number of players or spectators you can invite to a Playcast session but your home upload
                  bandwidth will generally limit the maximum number of viewers to 8-12. Beyond that, Open Broadcaster Software (OBS) or
                  Discord can be used to stream to larger groups.
                </p>
              </details>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};
